/**
 * Visitor Layout — data loading, contexts, always-present rendering.
 * Extracted from VisitorMapBySlug; rendered inside SheetOverlayProvider.
 * VisitorMapBySlug handles the outer provider + tab page switching.
 */
import {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import type { MapCanvasRef } from "../MapCanvas";
import {
  useVisitorSearch,
  useVisitorURLState,
  useVisitorPOIFiltering,
  useVisitorMapData,
  useCategoryNavigation,
  useVisitorSplash,
  useArrivalModal,
  useVisitorAnalytics,
  useGpsSessionTimer,
  useVisitorFeedback,
  useVisitorSponsors,
  useAccessibleMode,
  useVisitorPOISelection,
  useVisitorDeepLinks,
  useEmbedMode,
  useAccentColor,
} from "../../hooks/visitor";
import { useActiveMapQuery } from "../../hooks/visitor/useActiveMap";
import { useVisitorEvents } from "../../hooks/visitor/useVisitorEvents";
import { useVisitorFeatureFlags } from "../../hooks/visitor/useVisitorFeatureFlags";
import { useVisitorHandlers } from "../../hooks/visitor/useVisitorHandlers";
import { POIDetailSheet } from "./POIDetailSheet";
import { MorePage } from "./MorePage";
import { SearchPage } from "./SearchPage";
import { EventsPage } from "./EventsPage";
import { ControlHeader } from "./ControlHeader";
import { EventPeriodBanner } from "./EventPeriodBanner";
import { MapSection } from "./MapSection";
import { VisitorOverlays } from "./VisitorOverlays";
import { SponsorsBottomSheet } from "./SponsorsBottomSheet";
import { SponsorsFullScreen } from "./SponsorsFullScreen";
import { NotificationsTray } from "./NotificationsTray";
import { FAQBottomSheet } from "./FAQBottomSheet";
import { FAQFullScreen } from "./FAQFullScreen";
import { PausedMapSplash } from "./PausedMapSplash";
import { useUserLocation } from "../../hooks/useUserLocation";
import { usePOIDistances } from "../../hooks/usePOIDistances";
import { useSortPreference } from "../../hooks/useSortPreference";
import { useWayfinding } from "../../hooks/useWayfinding";
import { usePathNetwork } from "../../hooks/usePathNetwork";
import { RouteLayer } from "./RouteLayer";
import { DirectionsPanel } from "./DirectionsPanel";
import { RoutePreviewCard } from "./RoutePreviewCard";
import { useSheetOverlay } from "../../contexts/SheetOverlayContext";
import { BottomNavBar } from "./BottomNavBar";

import { useNetworkStatus } from "../../hooks/useNetworkStatus";
import { useBranding } from "../../hooks/useBranding";
import { useBookmarks } from "../../hooks/useBookmarks";
import {
  MAP_SIZE_PRESETS,
  getZoomOffset,
  type MapSize,
  type ThemeConfig,
} from "@wondermaps/maps";
import { toast } from "@wondermaps/ui";
import { NearestQuickActions } from "./NearestQuickActions";
import { useAnalyticsConsent } from "../../hooks/useAnalyticsConsent";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n/config";
import { useCuratedListLoader } from "../../hooks/visitor/useCuratedListLoader";
import { useAvailableCuratedLists } from "../../hooks/visitor/useAvailableCuratedLists";
import { CuratedListBrowserSheet } from "./CuratedListBrowserSheet";
import type { VisitorSearchParams } from "../../lib/visitor-search";

/** Map overlay that darkens based on active sheet snap point */
function MapOverlay() {
  const { overlayOpacity } = useSheetOverlay();
  if (overlayOpacity === 0) return null;
  return (
    <div
      className="absolute inset-0 bg-black pointer-events-none transition-opacity duration-300"
      style={{ opacity: overlayOpacity, zIndex: 5 }}
    />
  );
}

export interface VisitorLayoutProps {
  orgSlug: string;
  mapSlug: string;
  search: VisitorSearchParams;
}

export function VisitorLayout({
  orgSlug,
  mapSlug,
  search,
}: VisitorLayoutProps) {
  const { t } = useTranslation("map");
  const navigate = useNavigate();
  const {
    tab: tabParam,
    category: categoryParam,
    poi: poiParam,
    view: viewParam,
    ref: refParam,
    list: listParam,
    poiFilter: poiFilterParam,
  } = search;
  const { isEmbed } = useEmbedMode(search);

  // Pin resolved active map for session — direct navigation uses the URL's mapSlug,
  // but we still call useActiveMapQuery so the resolved mapId is cached for the session.
  const { mapId: activeMapId } = useActiveMapQuery(orgSlug);
  void activeMapId; // consumed by session cache; direct nav uses URL mapSlug

  const { isOffline } = useNetworkStatus();

  const mapRef = useRef<MapCanvasRef>(null);
  const routePreviewRef = useRef<HTMLDivElement>(null);

  // Search state (debounced)
  const {
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    searchInputRef,
    clearSearch,
  } = useVisitorSearch();

  // URL-synced view state (list view + tab switching)
  const {
    isListViewOpen,
    isSearchMode,
    openListView,
    closeListView,
    setTab,
  } = useVisitorURLState({
    initialViewParam: viewParam === "list" ? "list" : undefined,
    categoryParam,
    searchInputRef,
    onSearchClear: clearSearch,
  });

  // Menu open/close — derived from tab state for backward compat with existing handlers
  const isMenuOpen = tabParam === "more";
  const goToMap = useCallback(() => setTab("map"), [setTab]);

  // Fetch map data and transforms (moved up so pois available for POI selection)
  const {
    mapData,
    pausedData,
    layers,
    pois,
    landmarkPOIs,
    labels,
    illustrations,
    isFromCache,
    isError,
    isFetching,
    dataUpdatedAt,
    refetch,
  } = useVisitorMapData(orgSlug, mapSlug);

  // POI selection state + sheet stack
  const {
    selectedPOI,
    isPOISheetOpen,
    sheetContext,
    poisViewedRef,
    handleMapPOIClick,
    handleListPOIClick,
    handleFeaturedPOIClick,
    handleSheetClose,
    handleViewOnMap,
    reopenPOI,
    // Stack-based
    previous,
    isCuratedListBrowserOpen,
    isSponsorsSheetOpen,
    isFaqSheetOpen,
    openCuratedListBrowser,
    closeCuratedListBrowser,
    closeSponsorsSheet,
    closeFaqSheet,
    pushPOIFromSheet,
    handleBack,
    replace,
  } = useVisitorPOISelection(mapRef, closeListView, pois);

  // User location state
  const { location, isWatching, startWatching } = useUserLocation();

  // Convert hook location format to MapCanvas format
  const userLocation = useMemo(() => {
    if (!location) return undefined;
    return {
      lng: location.coords.longitude,
      lat: location.coords.latitude,
      accuracy: location.accuracy,
    };
  }, [
    location?.coords.longitude,
    location?.coords.latitude,
    location?.accuracy,
  ]);

  // Sort preference and distances
  const { sortMode, setSortMode } = useSortPreference();
  const userLocationForDistance = useMemo(() => {
    if (!userLocation) return null;
    return { lng: userLocation.lng, lat: userLocation.lat };
  }, [userLocation?.lng, userLocation?.lat]);

  // Feature flags
  const flags = useVisitorFeatureFlags(orgSlug);

  // Bookmarks state (per-org) - only active when flag enabled
  const { bookmarkedIds, toggleBookmark, isBookmarked, loadCuratedList } =
    useBookmarks(orgSlug);

  // Analytics consent
  const {
    status: consentStatus,
    accept: acceptConsent,
    dismiss: dismissConsent,
    trackPromptShown: trackConsentPromptShown,
  } = useAnalyticsConsent();
  const [consentBannerDismissed, setConsentBannerDismissed] = useState(false);

  // GPS session timer + visitor feedback
  const { hasReachedThreshold, gpsActiveMinutes } =
    useGpsSessionTimer(isWatching);
  const feedback = useVisitorFeedback({
    feedbackEnabled: flags.feedback,
    hasReachedThreshold,
    isListViewOpen,
    consentStatus,
    poisViewedRef,
    gpsActiveMinutes,
    orgSlug,
    mapSlug,
  });

  // Sponsors full-screen state (expand from sheet)
  const [isSponsorsFullScreen, setIsSponsorsFullScreen] = useState(false);

  // FAQ full-screen state (expand from sheet)
  const [isFaqFullScreen, setIsFaqFullScreen] = useState(false);

  // Notifications tray (plan 112)
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const openNotifications = useCallback(() => setNotificationsOpen(true), []);
  const closeNotifications = useCallback(() => setNotificationsOpen(false), []);

  // Canonical share URL — strips nav search params so recipients land on the map cleanly.
  // Title falls back through appName → orgName → i18n default.
  const shareUrl = useMemo(
    () =>
      typeof window === "undefined"
        ? ""
        : `${window.location.origin}${window.location.pathname}`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapSlug, orgSlug],
  );
  const shareTitle =
    mapData?.branding?.appName ??
    mapData?.organizationName ??
    t("share.defaultTitle", { ns: "common" });
  const shareText = t("share.defaultText", { ns: "common" });

  const getSponsorPoiName = useCallback(
    (poiId: string) => pois.find((p) => p.id === poiId)?.name,
    [pois],
  );

  // Curated list loader — merges POIs into bookmarks on URL param
  const [curatedListInfo, setCuratedListInfo] = useState<{
    name: string;
    description: string | null;
  } | null>(null);

  useCuratedListLoader({
    orgSlug,
    mapSlug,
    listSlug: listParam,
    onLoadPOIs: useCallback(
      (poiIds: string[], listName: string, description: string | null) => {
        loadCuratedList(poiIds);
        setCuratedListInfo({ name: listName, description });
        toast({
          title: t("curatedList.loaded", {
            name: listName,
            count: poiIds.length,
          }),
        });
      },
      [loadCuratedList, t],
    ),
    enabled: flags.curatedLists && flags.bookmarks && !!listParam,
  });

  // Capture initial URL params before any redirect (only POI or specific category is "intent")
  // "all" and undefined are not considered intent since they're default states
  const initialIntentRef = useRef<boolean | null>(null);
  if (initialIntentRef.current === null) {
    initialIntentRef.current = Boolean(
      isEmbed ||
      poiParam ||
      (categoryParam && categoryParam !== "all") ||
      listParam ||
      viewParam === "events" ||
      viewParam === "sponsors" ||
      viewParam === "faq",
    );
  }
  const hasIntent = initialIntentRef.current;

  // Derive zoom bounds from map size preset
  const mapSizePreset =
    MAP_SIZE_PRESETS[(mapData?.mapSize as MapSize) ?? "small"];
  const mapZoomOffset = getZoomOffset((mapData?.mapSize as MapSize) ?? "small");

  // Apply org's defaultLanguage when user has no saved preference
  useEffect(() => {
    const orgDefault = mapData?.orgSettings?.defaultLanguage;
    if (!orgDefault) return;
    const saved = localStorage.getItem("wondermaps_lang");
    if (!saved) {
      i18n.changeLanguage(orgDefault);
    }
  }, [mapData?.orgSettings?.defaultLanguage]);

  // Events processing (60s auto-refresh for "happening now" status)
  const {
    getPoiEvents,
    happeningNowEvents,
    happeningNowPoiIds,
    todaysEvents,
    upcomingEvents,
  } = useVisitorEvents(mapData?.events);

  // Available curated lists — consumed by MorePage quick-link
  const { data: availableCuratedLists } = useAvailableCuratedLists(
    orgSlug,
    mapSlug,
    flags.curatedLists,
  );

  // Category navigation (validation, redirects, pending state)
  const { effectiveCategory, navigateToCategory } = useCategoryNavigation({
    orgSlug,
    mapSlug,
    categoryParam,
    viewParam: viewParam === "list" ? "list" : undefined,
    tabParam,
    embedParam: search.embed,
    categories: mapData?.categories,
    hasMapData: !!mapData,
    listParam,
  });

  // Splash screen and welcome modal
  const {
    splashState,
    showSplash,
    welcomeModalOpen,
    isMapReady,
    dismissSplash,
    handleWelcomeDismiss,
  } = useVisitorSplash({
    orgSlug,
    isOffline,
    isFromCache,
    isError,
    hasMapData: !!mapData,
    isFetching,
    hasIntent,
  });

  // Apply venue branding (title, favicon, CSS vars)
  useBranding(mapData?.branding ?? null, mapData?.name);

  // Resolve accent color per touchpoint (cascade: theme primary → org brandColor)
  const accent = useAccentColor(
    mapData?.branding,
    mapData?.effectiveThemeConfig as ThemeConfig | undefined,
  );

  // Set drawer handle accent as CSS variable on root (portals read from :root)
  useEffect(() => {
    if (accent.drawerHandle) {
      document.documentElement.style.setProperty("--drawer-handle-color", accent.drawerHandle);
    } else {
      document.documentElement.style.removeProperty("--drawer-handle-color");
    }
    return () => {
      document.documentElement.style.removeProperty("--drawer-handle-color");
    };
  }, [accent.drawerHandle]);

  // Map sponsors (brand logos + analytics)
  const {
    splashSponsors,
    welcomeSponsors,
    menuSponsors,
    handleSponsorImpression,
    handleSponsorClick,
  } = useVisitorSponsors(flags.mapSponsors, mapData?.sponsors);

  // Headline sponsor for control header
  const headlineSponsor = useMemo(() => {
    const hs = mapData?.headlineSponsor;
    if (!hs?.logoUrl || !flags.mapSponsors) return null;
    return { logoUrl: hs.logoUrl, websiteUrl: hs.websiteUrl, name: hs.name };
  }, [mapData?.headlineSponsor, flags.mapSponsors]);

  // Deep-link handling (POI + curated + legacy ?menu= / ?view= redirects)
  useVisitorDeepLinks({
    poiParam,
    viewParam,
    listParam,
    pois,
    mapData,
    mapRef,
    replace,
  });

  // Analytics tracking and context (side effect only)
  useVisitorAnalytics({
    orgSlug,
    mapSlug,
    refParam,
    organizationSlug: mapData?.organizationSlug,
    dataUpdatedAt,
  });

  // Build path network from layers (for wayfinding)
  const { network: pathNetwork, nodeKindRefs } = usePathNetwork(mapData?.version.layers);

  // Accessible routing mode (overlay-driven + manual toggle + localStorage)
  const { accessibleMode, overlayAccessible, manualAccessibleMode, toggleManualAccessible } = useAccessibleMode();

  // Wayfinding hook (after POIs and path network are available)
  const {
    route,
    isNavigating,
    isPreviewing,
    destination,
    previewRoute,
    confirmNavigation,
    cancelNavigation,
    backToPreview,
    checkArrival,
    completedSegmentIndex,
    currentInstructionIndex,
    isFallbackRoute,
    isOffPath,
    reroute,
    remainingDistance,
    remainingTime,
  } = useWayfinding(pathNetwork, landmarkPOIs, accessibleMode, nodeKindRefs);

  // Auto-fit map bounds when route preview opens
  useEffect(() => {
    if (!isPreviewing || !route || route.path.length < 2) return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
    for (const [lng, lat] of route.path) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    // Defer one frame so RoutePreviewCard has rendered; measure actual sheet height
    // rather than deriving from viewport fraction (max-h-[40vh] is a cap, not the real height)
    const VAUL_HANDLE_SAFE_AREA = 24;
    const raf = requestAnimationFrame(() => {
      const sheetH = routePreviewRef.current?.getBoundingClientRect().height ?? 260;
      map.fitBounds(
        [[minLng, minLat], [maxLng, maxLat]],
        { padding: { top: 60, bottom: sheetH + VAUL_HANDLE_SAFE_AREA, left: 40, right: 40 }, duration: 600 }
      );
    });
    return () => cancelAnimationFrame(raf);
  }, [isPreviewing, route]);

  // Arrival modal (triggered by wayfinding arrival detection)
  const { showArrivalModal, arrivedPOI, handleArrivalClose } = useArrivalModal({
    location,
    isNavigating,
    destination,
    pois,
    checkArrival,
    cancelNavigation,
  });

  // Compute distances
  const distances = usePOIDistances(pois, userLocationForDistance);

  // POI filtering (search, category filter, counts, carousel categories)
  const {
    filteredPois: baseFilteredPois,
    poiCounts,
    carouselCategories,
    savedCount,
  } = useVisitorPOIFiltering({
    pois,
    categories: mapData?.categories ?? [],
    selectedCategory:
      effectiveCategory === "saved" || effectiveCategory === "happening-now"
        ? "all"
        : effectiveCategory,
    debouncedSearchQuery,
    bookmarkedPoiIds: flags.bookmarks ? bookmarkedIds : undefined,
    showSavedOnly: flags.bookmarks && effectiveCategory === "saved",
  });

  // Apply "happening-now" filter if selected
  const filteredPois = useMemo(() => {
    if (effectiveCategory !== "happening-now") return baseFilteredPois;
    return baseFilteredPois.filter((poi) => happeningNowPoiIds.has(poi.id));
  }, [baseFilteredPois, effectiveCategory, happeningNowPoiIds]);

  // Build POI names map for events sheet
  const poiNamesMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const poi of pois) {
      map.set(poi.id, poi.name);
    }
    return map;
  }, [pois]);

  // Apply ?poiFilter=<id> to event arrays for the events tab
  const filteredHappeningNowEvents = useMemo(
    () => (poiFilterParam ? happeningNowEvents.filter((e) => e.poiId === poiFilterParam) : happeningNowEvents),
    [happeningNowEvents, poiFilterParam],
  );
  const filteredTodaysEvents = useMemo(
    () => (poiFilterParam ? todaysEvents.filter((e) => e.poiId === poiFilterParam) : todaysEvents),
    [todaysEvents, poiFilterParam],
  );
  const filteredUpcomingEvents = useMemo(
    () => (poiFilterParam ? upcomingEvents.filter((e) => e.poiId === poiFilterParam) : upcomingEvents),
    [upcomingEvents, poiFilterParam],
  );

  // Resolve filter chip metadata. POI may have been deleted since the link
  // was shared — name falls back to a generic label so the chip + empty-state
  // still read correctly instead of leaving a trailing-space "Showing events at ".
  const poiFilterDescriptor = useMemo(() => {
    if (!poiFilterParam) return undefined;
    const resolvedName = poiNamesMap.get(poiFilterParam);
    return {
      id: poiFilterParam,
      name: resolvedName ?? t("events:poiUnavailable", { defaultValue: "this location" }),
      onClear: () => {
        void navigate({ to: ".", search: (prev) => ({ ...prev, poiFilter: undefined }) });
      },
    };
  }, [poiFilterParam, poiNamesMap, navigate, t]);

  // Navigate from POI sheet → events tab filtered to that POI
  const handleSeeAllEventsForPoi = useCallback(
    (poiId: string) => {
      void navigate({
        to: ".",
        search: (prev) => ({ ...prev, tab: "events", view: undefined, poi: undefined, poiFilter: poiId }),
      });
    },
    [navigate],
  );

  // Event handlers (analytics + state mutations)
  const {
    handleSelectCategory,
    handleEmergencyAction,
    handleEventNavigateToPoi,
    handleBookmarkToggle,
    handleGetDirections,
  } = useVisitorHandlers({
    pois,
    effectiveCategory,
    carouselCategories,
    happeningNowPoiIds,
    navigateToCategory,
    goToMap,
    handleMapPOIClick,
    handleSheetClose,
    pushPOIFromSheet,
    isBookmarked,
    toggleBookmark,
    selectedPOI,
    previewRoute,
  });

  // Gate map content — categories must load before map mounts for POI layers
  const hasMapContent = !!mapData && !!mapData.categories?.length;

  // Paused-map gate (plan 122) — render before any map UI so MapLibre chunk
  // doesn't load and overlays/sheets/bottom-nav stay collapsed.
  if (pausedData) {
    return (
      <PausedMapSplash
        orgSlug={pausedData.orgSlug}
        mapSlug={pausedData.mapSlug}
        orgName={pausedData.orgName}
        orgLogoUrl={pausedData.orgLogoUrl}
        pausedAt={pausedData.pausedAt}
        isEmbed={isEmbed}
      />
    );
  }

  return (
    <div className="relative h-dvh">
      {hasMapContent && (
        <>
          {/* Skip link for keyboard navigation */}
          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-gray-900 focus:rounded-md focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {t("common:skipToMainContent")}
          </a>

          {/* Map fills entire screen */}
          <MapSection
            ref={mapRef}
            id="main-content"
            className="absolute inset-0"
            layers={layers}
            pois={filteredPois}
            labels={labels}
            categories={mapData?.categories}
            defaultCenter={mapData.defaultCenter ?? undefined}
            defaultZoom={mapData.defaultZoom ?? undefined}
            minZoom={mapSizePreset.visitorMinZoom}
            maxZoom={mapSizePreset.visitorMaxZoom}
            zoomOffset={mapZoomOffset}
            onPOIClick={handleMapPOIClick}
            themeConfig={
              mapData.effectiveThemeConfig as ThemeConfig | undefined
            }
            userLocation={userLocation}
            selectedCategory={effectiveCategory}
            onClearCategory={() => handleSelectCategory("all")}
            onRecenter={() =>
              userLocation &&
              mapRef.current?.flyTo([userLocation.lng, userLocation.lat])
            }
            onLocationRequest={startWatching}
            mapInstance={mapRef.current?.getMap() ?? null}
            onContextLost={openListView}
            orgSlug={orgSlug}
            mapSlug={mapSlug}
            selectedPoiId={selectedPOI?.id}
            illustrations={illustrations}
          />

          {/* Dark overlay between map and sheets */}
          <MapOverlay />

          {/* Gradient scrim behind top header — hidden in embed */}
          {!isEmbed && (
            <div
              className="absolute top-0 inset-x-0 pointer-events-none z-[9] h-40 bg-gradient-to-b from-black/60 to-transparent"
            />
          )}

          {/* Floating header over map */}
          <ControlHeader
            className="absolute top-0 inset-x-0 z-10 pointer-events-none"
            isListViewOpen={isListViewOpen}
            isSearchMode={isSearchMode}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            searchInputRef={searchInputRef}
            onSearchOpen={openListView}
            onSearchClose={closeListView}
            categories={carouselCategories}
            selectedCategory={effectiveCategory ?? "all"}
            onSelectCategory={handleSelectCategory}
            poiCounts={poiCounts}
            logoUrl={mapData.branding?.logoUrl ?? undefined}
            appName={mapData.branding?.appName ?? undefined}
            savedCount={flags.bookmarks ? savedCount : 0}
            happeningNowCount={happeningNowPoiIds.size}
            isEmbed={isEmbed}
            headlineSponsor={headlineSponsor}
            brandAccentPills={accent.categoryPills}
            brandAccentIcons={accent.searchHeaderIcons}
            notifications={mapData.notifications ?? []}
            onBellClick={openNotifications}
            isTrayOpen={notificationsOpen}
            mapSlug={mapSlug}
            shareUrl={shareUrl}
            shareTitle={shareTitle}
            shareText={shareText}
          />

          {/* Event period banner — below header */}
          {!isEmbed && (
            <div
              className="absolute inset-x-0 z-9 flex flex-col items-center gap-1.5 pointer-events-none px-4 pt-1"
              style={{ top: "52px" }}
            >
              <EventPeriodBanner
                eventPeriod={mapData.eventPeriod ?? null}
                mapId={mapData.id}
              />
            </div>
          )}

          {/* POI Detail Sheet — visible when POI is in stack */}
          <POIDetailSheet
            poi={selectedPOI}
            isOpen={isPOISheetOpen}
            onClose={handleSheetClose}
            context={sheetContext ?? undefined}
            onViewOnMap={() => {
              // Switch to map tab + strip view sub-page, preserve poi
              if (tabParam !== "map" || viewParam) {
                void navigate({
                  to: ".",
                  search: { ...search, tab: "map", view: undefined },
                });
              }
              handleViewOnMap();
            }}
            hasPathNetwork={!!pathNetwork}
            onGetDirections={handleGetDirections}
            onRequestLocation={startWatching}
            isBookmarked={selectedPOI ? isBookmarked(selectedPOI.id) : false}
            onBookmarkToggle={(poiId) => handleBookmarkToggle(poiId, "detail")}
            bookmarksEnabled={flags.bookmarks}
            events={selectedPOI ? getPoiEvents(selectedPOI.id) : undefined}
            distance={selectedPOI ? distances.get(selectedPOI.id) : undefined}
            accessibleMode={accessibleMode}
            orgSlug={orgSlug}
            mapSlug={mapSlug}
            gpsActive={isWatching}
            ratingsEnabled={flags.poiRatings}
            onBack={previous ? handleBack : undefined}
            brandAccent={accent.poiCtaButton}
            onSeeAllEvents={handleSeeAllEventsForPoi}
          />

          {/* Nearest Quick Actions */}
          {!isEmbed && isListViewOpen && (
            <NearestQuickActions
              pois={pois}
              userLocation={userLocationForDistance}
              onSelectPOI={handleListPOIClick}
            />
          )}

          {/* Search Page — rendered when ?tab=search */}
          {tabParam === "search" && (
            <SearchPage
              mapSlug={mapSlug}
              logoUrl={mapData.branding?.logoUrl ?? undefined}
              appName={mapData.branding?.appName ?? undefined}
              notifications={mapData.notifications ?? []}
              onBellClick={openNotifications}
              isTrayOpen={notificationsOpen}
              shareUrl={shareUrl}
              shareTitle={shareTitle}
              shareText={shareText}
              pois={filteredPois}
              categories={carouselCategories}
              selectedCategory={effectiveCategory ?? "all"}
              onSelectCategory={handleSelectCategory}
              onSelectPOI={handleListPOIClick}
              poiCounts={poiCounts}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              searchInputRef={searchInputRef}
              userLocation={userLocationForDistance}
              distances={distances}
              sortMode={sortMode}
              onSortChange={setSortMode}
              savedCount={flags.bookmarks ? savedCount : 0}
              isBookmarked={isBookmarked}
              onBookmarkToggle={(poiId) => handleBookmarkToggle(poiId, "list")}
              bookmarksEnabled={flags.bookmarks}
              happeningNowCount={happeningNowPoiIds.size}
              accessibleMode={accessibleMode}
              brandAccent={accent.categoryPills}
              onRequestLocation={startWatching}
              curatedListInfo={curatedListInfo}
            />
          )}

          {/* More Page — rendered when ?tab=more. */}
          {!isEmbed && mapData.orgSettings?.visitor_menu && isMenuOpen && (
            <MorePage
              mapSlug={mapSlug}
              logoUrl={mapData.branding?.logoUrl ?? undefined}
              appName={mapData.branding?.appName ?? undefined}
              orgName={mapData.organizationName ?? ""}
              notifications={mapData.notifications ?? []}
              onBellClick={openNotifications}
              isTrayOpen={notificationsOpen}
              shareUrl={shareUrl}
              shareTitle={shareTitle}
              shareText={shareText}
              menuConfig={mapData.orgSettings.visitor_menu}
              faqConfig={mapData.orgSettings?.faq}
              showAttribution={!mapData.branding?.hidePoweredBy}
              operatingMode={mapData.operatingMode}
              operatingConfig={mapData.operatingConfig ?? null}
              timezone={mapData.timezone}
              emergencyActions={
                flags.emergencyActions
                  ? (mapData.emergencyActions ?? [])
                  : undefined
              }
              onEmergencyAction={handleEmergencyAction}
              savedCount={flags.bookmarks ? savedCount : 0}
              onSavedClick={
                flags.bookmarks
                  ? () => {
                      // Single combined navigate — sequential setTab + handleSelectCategory
                      // would race because navigateToCategory re-merges with the current
                      // (still "more") tabParam, overwriting the tab switch.
                      void navigate({
                        to: ".",
                        search: {
                          ...search,
                          tab: "search",
                          category: "saved",
                          poi: undefined,
                          view: undefined,
                        },
                      });
                    }
                  : undefined
              }
              curatedListsCount={
                flags.curatedLists ? (availableCuratedLists?.length ?? 0) : 0
              }
              onCuratedListsClick={() => {
                goToMap();
                openCuratedListBrowser();
              }}
              sponsors={menuSponsors}
              onSponsorClick={handleSponsorClick}
              onSponsorImpression={handleSponsorImpression}
              getSponsorPoiName={getSponsorPoiName}
              onSponsorPoiClick={(poiId) => {
                const poi = pois.find((p) => p.id === poiId);
                if (poi) handleFeaturedPOIClick(poi);
              }}
              multilingualEnabled={flags.multilingual}
              availableLanguages={
                mapData.orgSettings.availableLanguages as
                  | import("../../i18n/config").SupportedLanguage[]
                  | undefined
              }
              brandAccentCtaSecondary={accent.morePageCtaSecondary}
              brandAccentMenuIcons={accent.menuIcons}
              brandAccentSocialCard={accent.socialCardBranded}
              brandAccentSocialCardBg={accent.socialCardBg}
              brandAccentPoweredBy={accent.poweredByFooter}
              brandPrimary={accent.brandPrimary}
            />
          )}
          {/* Sponsors Bottom Sheet */}
          {menuSponsors && (
            <SponsorsBottomSheet
              open={isSponsorsSheetOpen}
              onClose={closeSponsorsSheet}
              sponsors={menuSponsors}
              onSponsorClick={handleSponsorClick}
              getPoiName={getSponsorPoiName}
              onPoiClick={(poiId) => {
                closeSponsorsSheet();
                const poi = pois.find((p) => p.id === poiId);
                if (poi) handleFeaturedPOIClick(poi);
              }}
              onExpand={() => {
                closeSponsorsSheet();
                setIsSponsorsFullScreen(true);
              }}
            />
          )}

          {/* Sponsors Full Screen */}
          {menuSponsors && isSponsorsFullScreen && (
            <SponsorsFullScreen
              sponsors={menuSponsors}
              onSponsorClick={handleSponsorClick}
              getPoiName={getSponsorPoiName}
              onPoiClick={(poiId) => {
                setIsSponsorsFullScreen(false);
                const poi = pois.find((p) => p.id === poiId);
                if (poi) handleFeaturedPOIClick(poi);
              }}
              onClose={() => setIsSponsorsFullScreen(false)}
            />
          )}

          {/* FAQ Bottom Sheet */}
          {mapData.orgSettings?.faq?.enabled && mapData.orgSettings.faq.items.length > 0 && (
            <FAQBottomSheet
              open={isFaqSheetOpen}
              onClose={closeFaqSheet}
              faqConfig={mapData.orgSettings.faq}
              lang={i18n.language}
              onExpand={() => {
                closeFaqSheet();
                setIsFaqFullScreen(true);
              }}
            />
          )}

          {/* FAQ Full Screen */}
          {mapData.orgSettings?.faq?.enabled && mapData.orgSettings.faq.items.length > 0 && isFaqFullScreen && (
            <FAQFullScreen
              faqConfig={mapData.orgSettings.faq}
              lang={i18n.language}
              onClose={() => setIsFaqFullScreen(false)}
            />
          )}

          {/* Notifications Tray (plan 112) */}
          <NotificationsTray
            open={notificationsOpen}
            notifications={mapData.notifications ?? []}
            mapSlug={mapSlug}
            onClose={closeNotifications}
          />

          {/* Events Page — rendered when ?tab=events */}
          {tabParam === "events" && (
            <EventsPage
              mapSlug={mapSlug}
              logoUrl={mapData.branding?.logoUrl ?? undefined}
              appName={mapData.branding?.appName ?? undefined}
              notifications={mapData.notifications ?? []}
              onBellClick={openNotifications}
              isTrayOpen={notificationsOpen}
              shareUrl={shareUrl}
              shareTitle={shareTitle}
              shareText={shareText}
              happeningNowEvents={filteredHappeningNowEvents}
              todaysEvents={filteredTodaysEvents}
              upcomingEvents={filteredUpcomingEvents}
              onNavigateToPoi={handleEventNavigateToPoi}
              poiNames={poiNamesMap}
              poiFilter={poiFilterDescriptor}
            />
          )}

          {/* Curated List Browser Sheet — hidden in embed */}
          {!isEmbed && flags.curatedLists && (
            <CuratedListBrowserSheet
              isOpen={isCuratedListBrowserOpen}
              onClose={closeCuratedListBrowser}
              orgSlug={orgSlug}
              mapSlug={mapSlug}
              pois={pois}
              bookmarkedIds={bookmarkedIds}
              toggleBookmark={toggleBookmark}
              onLoadPOIs={loadCuratedList}
              onSelectPOI={(poi) =>
                pushPOIFromSheet(poi, {
                  type: "poi",
                  poiId: poi.id,
                  context: "curated",
                })
              }
              initialExpandSlug={listParam}
            />
          )}

          {/* Wayfinding Route Layer — visible during preview or navigation */}
          {!isEmbed && (isPreviewing || isNavigating) && route && mapRef.current?.getMap() && (
            <RouteLayer
              route={route}
              map={mapRef.current.getMap()!}
              completedSegmentIndex={completedSegmentIndex}
            />
          )}

          {/* Route Preview Card — shown before navigation starts */}
          {!isEmbed && isPreviewing && route && destination && (
            <div ref={routePreviewRef}>
              <RoutePreviewCard
                route={route}
                destinationId={destination.id}
                destinationName={destination.name}
                categoryName={destination.categoryName}
                categoryIcon={destination.categoryIcon}
                onStart={confirmNavigation}
                onClose={cancelNavigation}
                onBackToPOI={() => {
                  const id = destination.id;
                  cancelNavigation();
                  reopenPOI(id);
                }}
                accessibleMode={accessibleMode}
                overlayAccessible={overlayAccessible}
                manualAccessibleMode={manualAccessibleMode}
                isFallbackRoute={isFallbackRoute}
                onToggleAccessible={toggleManualAccessible}
                brandAccent={accent.directionsUI}
                isBookmarked={isBookmarked(destination.id)}
                onBookmarkToggle={(id) => handleBookmarkToggle(id, "detail")}
                bookmarksEnabled={flags.bookmarks}
              />
            </div>
          )}

          {/* Wayfinding Directions Panel — active navigation */}
          {!isEmbed && isNavigating && route && (
            <DirectionsPanel
              route={route}
              destinationId={destination?.id}
              destinationName={destination?.name}
              categoryName={destination?.categoryName}
              categoryIcon={destination?.categoryIcon}
              onClose={cancelNavigation}
              onCancel={backToPreview}
              accessibleMode={accessibleMode}
              overlayAccessible={overlayAccessible}
              manualAccessibleMode={manualAccessibleMode}
              isFallbackRoute={isFallbackRoute}
              onToggleAccessible={toggleManualAccessible}
              currentInstructionIndex={currentInstructionIndex}
              isOffPath={isOffPath}
              onReroute={reroute}
              remainingDistance={remainingDistance}
              remainingTime={remainingTime}
              brandAccent={accent.directionsUI}
              isBookmarked={destination ? isBookmarked(destination.id) : false}
              onBookmarkToggle={(id) => handleBookmarkToggle(id, "detail")}
              bookmarksEnabled={flags.bookmarks}
            />
          )}
        </>
      )}

      {/* Bottom navigation bar — visible in embed (search/events/more still useful) */}
      <BottomNavBar
        activeTab={tabParam}
        brandAccent={accent.bottomNavActive}
      />

      {/* Overlays: splash, welcome, consent, feedback, offline, install, arrival */}
      {!isEmbed && (
        <VisitorOverlays
          showSplash={showSplash}
          splashState={splashState}
          isFromCache={isFromCache}
          branding={mapData?.branding ?? null}
          onRetry={() => refetch()}
          onDismissSplash={() => dismissSplash()}
          splashSponsors={splashSponsors}
          welcomeSponsors={welcomeSponsors}
          onSponsorImpression={handleSponsorImpression}
          welcomeModalOpen={welcomeModalOpen}
          isMapReady={isMapReady}
          mapName={mapData?.name ?? "this map"}
          onWelcomeDismiss={handleWelcomeDismiss}
          onSponsorClick={handleSponsorClick}
          consentStatus={consentStatus}
          consentBannerDismissed={consentBannerDismissed}
          onAcceptConsent={acceptConsent}
          onDismissConsent={() => {
            setConsentBannerDismissed(true);
            dismissConsent();
          }}
          onConsentPromptShown={trackConsentPromptShown}
          showFeedbackBanner={feedback.showBanner}
          feedbackPhase={feedback.feedbackPhase}
          rating={feedback.rating}
          comment={feedback.comment}
          setRating={feedback.setRating}
          setComment={feedback.setComment}
          submitFeedback={feedback.submit}
          skipFeedback={feedback.skip}
          dismissFeedback={feedback.dismiss}
          showArrivalModal={showArrivalModal}
          arrivedPOI={arrivedPOI}
          onArrivalClose={handleArrivalClose}
          brandAccentPoweredBy={accent.poweredByFooter}
        />
      )}

      {/* Powered by Wondermaps — embed only */}
      {isEmbed && (
        <a
          href="https://wondermaps.co"
          target="_blank"
          rel="noopener noreferrer"
          className="absolute bottom-2 right-2 z-10 rounded-full bg-background/90 backdrop-blur-sm px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm hover:text-foreground transition-colors"
          style={accent.poweredByFooter ? { color: accent.poweredByFooter } : undefined}
        >
          Powered by Wondermaps
        </a>
      )}
    </div>
  );
}
